import axios from 'axios';
import { API_BASE_URL } from './config';

class PartnerService {
    constructor() {
        this.axiosInstance = axios.create({
            baseURL: API_BASE_URL,
            withCredentials: true
        });
    }

    // Prüfen, ob der aktuelle Benutzer Partner-Status hat
    async isPartner() {
        try {
            const response = await this.axiosInstance.get('/webusers/is-partner');
            return response.data.isPartner || false;
        } catch (error) {
            console.error('Fehler beim Abrufen des Partner-Status:', error);
            return false;
        }
    }

    // Kunden eines Partners abrufen
    async getCustomers(partnerId) {
        try {
            // Wenn keine partnerId angegeben ist, verwende die ID des eingeloggten Benutzers
            const partnerIdToUse = partnerId || (await this.getCurrentUserId());
            const response = await this.axiosInstance.get(`/webusers/partner/customers/${partnerIdToUse}`);
            return response.data;
        } catch (error) {
            console.error('Fehler beim Abrufen der Kunden:', error);
            
            // Spezifische Fehlermeldung für 403 Fehler
            if (error.response && error.response.status === 403) {
                throw new Error('Sie benötigen Partner-Berechtigungen für diese Funktion');
            }
            
            throw error;
        }
    }
    
    // Hilfsmethode, um die aktuelle Benutzer-ID zu erhalten
    async getCurrentUserId() {
        try {
            const response = await this.axiosInstance.get('/webusers/profile');
            return response.data.id;
        } catch (error) {
            console.error('Fehler beim Abrufen des Benutzerprofils:', error);
            throw error;
        }
    }
    
    // Einzelnen Kunden abrufen
    async getCustomer(customerId) {
        try {
            // First get all customers
            const customers = await this.getCustomers();
            // Then find the specific customer
            const customer = customers.find(c => c.id === parseInt(customerId) || c.id === customerId);
            
            if (!customer) {
                throw new Error('Kunde nicht gefunden');
            }
            
            return customer;
        } catch (error) {
            console.error('Fehler beim Abrufen des Kunden:', error);
            throw error;
        }
    }

    // Neuen Kunden für einen Partner erstellen
    async createCustomer(customerData) {
        try {
            const response = await this.axiosInstance.post('/webusers/partner/customers', customerData);
            return response.data;
        } catch (error) {
            console.error('Fehler beim Erstellen des Kunden:', error);
            throw error;
        }
    }

    // IAAS-Statistiken eines Partners abrufen
    async getIaasStats(partnerId) {
        try {
            const response = await this.axiosInstance.get(`/iaas-service/partner-stats/${partnerId}`);
            return response.data;
        } catch (error) {
            console.error('Fehler beim Abrufen der IAAS-Statistiken:', error);
            return { count: 0 };
        }
    }

    // SAAS-Statistiken eines Partners abrufen
    async getSaasStats(partnerId) {
        try {
            const response = await this.axiosInstance.get(`/saas-service/partner-stats/${partnerId}`);
            return response.data;
        } catch (error) {
            console.error('Fehler beim Abrufen der SAAS-Statistiken:', error);
            return { count: 0, details: { saas: 0, ecm: 0, epm: 0, auto: 0, sboffice: 0 } };
        }
    }

    // Eigene White-Label-Einstellungen laden (Partner)
    async getMySettings() {
        try {
            const response = await this.axiosInstance.get('/api/partner-settings/my');
            return response.data;
        } catch (error) {
            console.error('Fehler beim Laden der Partner-Settings:', error);
            return null;
        }
    }

    // White-Label-Einstellungen speichern (Upsert)
    async saveMySettings(data) {
        const response = await this.axiosInstance.put('/api/partner-settings/my', data);
        return response.data;
    }

    // Logo hochladen
    async uploadLogo(file) {
        const formData = new FormData();
        formData.append('logo', file);
        const response = await this.axiosInstance.post(
            '/api/partner-settings/my/logo',
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        return response.data;
    }

    // Favicon hochladen
    async uploadFavicon(file) {
        const formData = new FormData();
        formData.append('favicon', file);
        const response = await this.axiosInstance.post(
            '/api/partner-settings/my/favicon',
            formData,
            { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        return response.data;
    }

    // IAAS-Services der Kunden eines Partners abrufen
    async getCustomerServices(serviceType) {
        try {
            const endpoint = serviceType === 'iaas' ? 
                '/iaas-service/customer-services' : 
                '/saas-service/customer-services';
                
            const response = await this.axiosInstance.get(endpoint);
            return response.data;
        } catch (error) {
            console.error(`Fehler beim Abrufen der ${serviceType.toUpperCase()}-Kunden-Services:`, error);
            
            // Fehler weiterwerfen, damit er in der Komponente behandelt werden kann
            if (error.response && error.response.status === 403) {
                throw new Error('Sie benötigen Partner-Berechtigungen für diese Funktion');
            }
            
            throw error;
        }
    }
}

export const partnerService = new PartnerService();
